import { shortDate, units } from "@/lib/format"
import type { AverageSales, Buying, OrderBy, Purchase, Stockout } from "../types"
import { addDays, daysBetween } from "./dates"

// What each of the three summary cards says. Without a plan for the market
// the plan-based numbers read "No plan", never 0.
export interface CardText {
  value: string
  lines: string[]
  // Red: runs out before an order placed today could land.
  alert?: boolean
}

export interface CardLine {
  text: string
  alert: boolean
}

function dayCount(n: number): string {
  return `${n} ${n === 1 ? "day" : "days"}`
}

// Two decimals under 1 a day, one under 10, whole above: a slow product
// still shows a number, never "0.0".
function dailyRate(daily: number): string {
  const digits = daily < 1 ? 2 : daily < 10 ? 1 : 0
  return daily.toLocaleString("en-US", { maximumFractionDigits: digits })
}

// Stock on hand, how many days it lasts (the out-of-stock card's days), and
// the average units sold or gifted a day over the last 90 days, out-of-stock
// days left out (Inventory::StockHealth).
export function stockCard(onHand: number, stockout: Stockout | null, average: AverageSales | null): CardText {
  const value = units(onHand)
  if (!stockout) return { value, lines: ["No plan or recent sales"] }
  const cover = `${dayCount(stockout.days)} of cover`
  const lines = [stockout.after_plan ? `More than ${cover}` : cover]
  if (average) lines.push(`Average ${dailyRate(average.daily_sales)} a day, sold or gifted`)
  return { value, lines }
}

// Where the projected stock comes from: the plan, or without one the average
// daily sales of the last 90 days. Null with neither.
export type StockSource = "plan" | "average"

export const STOCK_SOURCE_LABEL: Record<StockSource, string> = {
  plan: "From the plan",
  average: "From average sold or gifted, last 90 days",
}

export function stockSource(buying: Buying, average: AverageSales | null): StockSource | null {
  if (buying.market.planned_units !== null) return "plan"
  return average ? "average" : null
}

// The day this market runs out of stock: the Inventory page's number.
export function stockoutCard(stockout: Stockout | null, buying: Buying, today: string): CardText {
  if (!stockout) return { value: "No plan", lines: ["Nothing sold or gifted in the last 90 days"] }
  const stockoutOn = addDays(today, stockout.days)
  if (stockout.after_plan) {
    return { value: `After ${shortDate(stockoutOn, today)}`, lines: [`Lasts past the plan, more than ${dayCount(stockout.days)}`] }
  }
  return {
    value: shortDate(stockoutOn, today),
    lines: [stockout.days === 0 ? "Today" : `In ${dayCount(stockout.days)}`],
    alert: stockoutOn < buying.order_lands_on,
  }
}

function allMarketsLine(purchase: Purchase): string {
  if (purchase.quantity === null) return "All markets: unknown"
  const moq = purchase.raised_to_moq ? " (raised to MOQ)" : ""
  return `All markets: ${units(purchase.quantity)}${moq}`
}

// This market's share of the Purchase Plan's order, then the order for every
// market that needs buying. The landing date shows only when there is an
// order to place.
export function purchaseCard(purchase: Purchase | null, today: string): CardText {
  if (!purchase) return { value: "Not needed", lines: ["No market needs buying"] }
  const lines = [allMarketsLine(purchase)]
  if ((purchase.quantity ?? 0) > 0) lines.push(`Order lands ${shortDate(purchase.order_lands_on, today)} if ordered today`)
  if (!purchase.market_needs_buying) return { value: "Not needed here", lines }
  return { value: purchase.market_quantity === null ? "Unknown" : units(purchase.market_quantity), lines }
}

// Under the out-of-stock card: the Inventory grid's order-by date (run-out
// minus lead time) on the average sales or, without them, the plan, and a stockout that
// ends before any new order could land.
export function orderByLines(orderBy: OrderBy | null, today: string): CardLine[] {
  if (!orderBy) return []
  const lines: CardLine[] = []
  if (orderBy.reorder_iso) {
    const late = daysBetween(orderBy.reorder_iso, today)
    if (late > 0) {
      lines.push({ text: `Overdue ${dayCount(late)} · order by ${shortDate(orderBy.reorder_iso, today)}`, alert: true })
    } else {
      lines.push({ text: `Order by ${late === 0 ? "today" : shortDate(orderBy.reorder_iso, today)}`, alert: false })
    }
  }
  if (orderBy.gap) {
    const { start_iso, end_iso } = orderBy.gap
    lines.push({ text: `Out ${shortDate(start_iso, today)} – ${shortDate(end_iso, today)}, before a new order can land`, alert: true })
  }
  return lines
}
